/**
 * Comprehensive Job Title Synonym Groups
 *
 * Each array = one group of titles that are commonly used interchangeably or refer
 * to the same (or nearly identical) role across different companies / regions.
 *
 * CHANGELOG vs v1:
 *  - Fixed 154 cross-group duplicate entries (ambiguous matching bug)
 *  - Removed iOS/Android from the generic Mobile group (they have own groups)
 *  - Deduplicated Firmware Engineer, Systems Engineer, Operations Analyst, etc.
 *  - Added 15 new industry sections (31-45)
 *  - Added ~80 missing roles within existing sections
 *
 * Sections:
 *   1.  C-Suite & Executive Leadership
 *   2.  Engineering - Software
 *   3.  Engineering - Infrastructure / Platform / Security
 *   4.  Engineering - Hardware / Embedded / Firmware
 *   5.  Engineering Management
 *   6.  Product Management
 *   7.  Design & UX
 *   8.  Data & Analytics
 *   9.  Artificial Intelligence & Machine Learning
 *  10.  Research & Science
 *  11.  Sales
 *  12.  Marketing
 *  13.  Customer Success & Support
 *  14.  Human Resources & People Operations
 *  15.  Recruiting & Talent Acquisition
 *  16.  Finance & Accounting
 *  17.  Legal & Compliance
 *  18.  Operations & Strategy
 *  19.  Project & Program Management
 *  20.  IT & Systems Administration
 *  21.  Healthcare & Clinical
 *  22.  Education & Training
 *  23.  Construction, Architecture & Real Estate
 *  24.  Manufacturing & Supply Chain
 *  25.  Hospitality & Food Service
 *  26.  Media, Communications & PR
 *  27.  Creative & Content
 *  28.  Administrative & Executive Support
 *  29.  Non-Profit & Government
 *  30.  Trades & Skilled Labor
 *  31.  Consulting
 *  32.  Banking & Financial Services
 *  33.  Insurance
 *  34.  Energy, Oil & Gas & Renewables
 *  35.  Aerospace & Defense
 *  36.  Telecommunications
 *  37.  Retail & E-commerce
 *  38.  Veterinary & Animal Care
 *  39.  Beauty, Wellness & Personal Care
 *  40.  Sports, Fitness & Recreation
 *  41.  Music & Audio
 *  42.  Film, TV & Theatre
 *  43.  Fashion & Apparel
 *  44.  Publishing & Books
 *  45.  Childcare & Early Education
 */
const JOB_TITLE_SYNONYM_GROUPS: string[][] = [
    // ---------------------------------------------------------------------------
    // 1. C-SUITE & EXECUTIVE LEADERSHIP
    // ---------------------------------------------------------------------------
    ['CEO', 'Chief Executive Officer', 'Managing Director', 'President', 'Executive Director'],
    ['CFO', 'Chief Financial Officer', 'VP of Finance', 'Vice President of Finance', 'Head of Finance'],
    ['COO', 'Chief Operating Officer', 'VP of Operations', 'Vice President of Operations', 'Head of Operations'],
    [
        'CTO',
        'Chief Technology Officer',
        'VP of Engineering',
        'Vice President of Engineering',
        'Head of Technology',
        'Head of Engineering',
    ],
    ['CPO', 'Chief Product Officer', 'VP of Product', 'Vice President of Product', 'Head of Product'],
    ['CMO', 'Chief Marketing Officer', 'VP of Marketing', 'Vice President of Marketing', 'Head of Marketing'],
    [
        'CHRO',
        'Chief Human Resources Officer',
        'Chief People Officer',
        'VP of HR',
        'VP of Human Resources',
        'Vice President of People',
        'Head of People',
    ],
    ['CIO', 'Chief Information Officer', 'VP of IT', 'Vice President of Information Technology', 'Head of IT'],
    ['CSO', 'Chief Sales Officer', 'VP of Sales', 'Vice President of Sales', 'Head of Sales'],
    ['CLO', 'Chief Legal Officer', 'General Counsel', 'VP of Legal', 'Vice President of Legal', 'Head of Legal'],
    [
        'CCO',
        'Chief Customer Officer',
        'VP of Customer Success',
        'Vice President of Customer Experience',
        'Head of Customer Success',
    ],
    ['CCO', 'Chief Compliance Officer', 'Head of Compliance', 'VP of Compliance'],
    [
        'CISO',
        'Chief Information Security Officer',
        'VP of Security',
        'Vice President of Security',
        'Head of Security',
        'Head of Cybersecurity',
    ],
    ['CDO', 'Chief Data Officer', 'VP of Data', 'Vice President of Data', 'Head of Data'],
    ['CRO', 'Chief Revenue Officer', 'VP of Revenue', 'Vice President of Revenue', 'Head of Revenue'],
    ['CAO', 'Chief Analytics Officer', 'Head of Analytics', 'VP of Analytics'],
    ['CBO', 'Chief Business Officer', 'Head of Business Development', 'VP of Business Development'],
    ['CGO', 'Chief Growth Officer', 'Head of Growth', 'VP of Growth'],
    ['CXO', 'Chief Experience Officer', 'Head of Experience', 'VP of Experience'],
    ['CAIO', 'Chief AI Officer', 'Chief Artificial Intelligence Officer', 'Head of AI', 'VP of AI'],
    ['CSuO', 'Chief Sustainability Officer', 'Head of Sustainability', 'VP of Sustainability', 'ESG Director'],
    ['CRO (Risk)', 'Chief Risk Officer', 'Head of Risk', 'VP of Risk Management'],
    ['CDO (Digital)', 'Chief Digital Officer', 'Head of Digital', 'VP of Digital Transformation'],
    ['Chairman', 'Chairman of the Board', 'Board Chair', 'Chairperson'],
    ['Founder', 'Co-Founder', 'Founding Partner'],
    ['Partner', 'Managing Partner', 'General Partner', 'Senior Partner'],
    ['Managing Principal', 'Senior Principal', 'Principal Consultant'],
    ['VP', 'Vice President', 'SVP', 'Senior Vice President', 'EVP', 'Executive Vice President'],
    ['Board Member', 'Board Director', 'Non-Executive Director', 'Independent Director', 'NED'],

    // ---------------------------------------------------------------------------
    // 2. ENGINEERING - SOFTWARE
    // ---------------------------------------------------------------------------
    ['Software Engineer', 'Software Developer', 'Developer', 'Programmer', 'Coder', 'Application Developer'],
    [
        'Junior Software Engineer',
        'Junior Developer',
        'Associate Software Engineer',
        'Associate Developer',
        'Entry-Level Developer',
        'SWE I',
    ],
    ['Mid-Level Software Engineer', 'Software Engineer II', 'SWE II'],
    ['Senior Software Engineer', 'Senior Software Developer', 'Senior Developer', 'Senior SWE', 'SWE III'],
    ['Staff Engineer', 'Staff Software Engineer', 'Principal Engineer', 'Principal Software Engineer'],
    ['Distinguished Engineer', 'Fellow', 'Software Fellow', 'Senior Principal Engineer'],
    [
        'Software Architect',
        'Enterprise Architect',
        'Application Architect',
        'Technical Architect',
        'Solution Architect',
    ],
    [
        'Frontend Engineer',
        'Frontend Developer',
        'Front-End Engineer',
        'Front-End Developer',
        'UI Engineer',
        'UI Developer',
        'Client-Side Developer',
    ],
    ['Backend Engineer', 'Backend Developer', 'Back-End Engineer', 'Back-End Developer', 'Server-Side Developer'],
    [
        'Full Stack Engineer',
        'Full Stack Developer',
        'Fullstack Engineer',
        'Fullstack Developer',
        'Full-Stack Developer',
    ],
    ['Mobile Engineer', 'Mobile Developer', 'App Developer', 'Cross-Platform Mobile Developer'],
    ['iOS Developer', 'iOS Engineer', 'Swift Developer', 'Apple Developer'],
    ['Android Developer', 'Android Engineer', 'Kotlin Developer'],
    ['React Native Developer', 'Flutter Developer', 'Xamarin Developer'],
    ['Web Developer', 'Web Engineer', 'Website Developer'],
    ['API Developer', 'Integration Engineer', 'Integration Developer', 'Middleware Developer'],
    ['Game Developer', 'Game Engineer', 'Game Programmer', 'Unity Developer', 'Unreal Developer'],
    [
        'Blockchain Developer',
        'Web3 Developer',
        'Smart Contract Developer',
        'Solidity Developer',
        'Crypto Developer',
        'DeFi Developer',
    ],
    [
        'AR/VR Developer',
        'Augmented Reality Engineer',
        'Virtual Reality Engineer',
        'XR Developer',
        'Mixed Reality Engineer',
    ],
    ['QA Engineer', 'Quality Assurance Engineer', 'Test Engineer', 'Software Tester', 'QA Analyst'],
    [
        'QA Automation Engineer',
        'Automation Test Engineer',
        'SDET',
        'Software Development Engineer in Test',
        'Test Automation Engineer',
    ],
    ['QA Lead', 'QA Manager', 'Quality Assurance Lead', 'Quality Assurance Manager'],
    ['Build Engineer', 'Release Engineer', 'CI/CD Engineer', 'Build & Release Engineer'],
    ['Solutions Engineer', 'Technical Solutions Engineer'],
    ['Implementation Engineer', 'Implementation Specialist', 'Deployment Engineer'],
    ['Technical Lead', 'Tech Lead', 'Lead Engineer', 'Lead Software Engineer', 'Lead Developer'],
    ['Cloud Engineer', 'Cloud Developer', 'AWS Engineer', 'Azure Engineer', 'GCP Engineer'],
    ['Performance Engineer', 'Performance Optimization Engineer', 'Load Testing Engineer'],
    ['Accessibility Engineer', 'a11y Engineer', 'Inclusive Engineering Specialist'],
    ['Security Software Engineer', 'Application Security Engineer', 'AppSec Engineer'],
    ['Compiler Engineer', 'Programming Language Engineer', 'Runtime Engineer'],

    // ---------------------------------------------------------------------------
    // 3. ENGINEERING - INFRASTRUCTURE / PLATFORM / SECURITY
    // ---------------------------------------------------------------------------
    ['DevOps Engineer', 'Platform Engineer', 'Infrastructure Engineer', 'Cloud Infrastructure Engineer'],
    ['Site Reliability Engineer', 'SRE', 'Reliability Engineer'],
    ['FinOps Engineer', 'Cloud Cost Engineer', 'Cloud Economics Analyst'],
    ['Observability Engineer', 'Monitoring Engineer', 'Telemetry Engineer'],
    ['Network Engineer', 'Network Administrator', 'Network Architect', 'Network Infrastructure Engineer'],
    ['Systems Administrator', 'Sysadmin', 'Linux Administrator', 'Linux Engineer', 'Unix Engineer'],
    ['Database Administrator', 'DBA', 'Database Engineer', 'Database Developer'],
    ['Security Engineer', 'Cybersecurity Engineer', 'Infrastructure Security Engineer'],
    ['Penetration Tester', 'Pen Tester', 'Ethical Hacker', 'Red Team Engineer', 'Offensive Security Engineer'],
    ['Security Analyst', 'Cybersecurity Analyst', 'Information Security Analyst', 'SOC Analyst', 'Threat Analyst'],
    ['Security Architect', 'Cybersecurity Architect', 'Information Security Architect'],
    ['Cloud Architect', 'AWS Architect', 'Azure Architect', 'GCP Architect'],
    ['Storage Engineer', 'Storage Administrator', 'Data Center Engineer'],
    ['Virtualization Engineer', 'VMware Engineer', 'Cloud Virtualization Specialist'],
    ['Network Security Engineer', 'Firewall Engineer', 'VPN Engineer'],
    ['Identity & Access Management Engineer', 'IAM Engineer', 'Identity Engineer'],
    ['Kubernetes Engineer', 'Container Engineer', 'Docker Engineer', 'Orchestration Engineer'],
    ['Terraform Engineer', 'Infrastructure as Code Engineer', 'IaC Engineer'],

    // ---------------------------------------------------------------------------
    // 4. ENGINEERING - HARDWARE / EMBEDDED / FIRMWARE
    // ---------------------------------------------------------------------------
    ['Hardware Engineer', 'Electrical Engineer', 'Circuit Design Engineer', 'Electronics Engineer'],
    ['Firmware Engineer', 'Embedded Systems Engineer', 'Embedded Software Engineer', 'RTOS Engineer'],
    ['FPGA Engineer', 'FPGA Developer', 'VLSI Engineer', 'RTL Designer'],
    ['RF Engineer', 'Radio Frequency Engineer', 'Wireless Engineer', 'Antenna Engineer'],
    ['Signal Processing Engineer', 'DSP Engineer', 'Digital Signal Processing Engineer'],
    ['PCB Designer', 'PCB Engineer', 'Board Design Engineer', 'Circuit Board Designer'],
    ['Hardware Test Engineer', 'Validation Engineer', 'Verification Engineer'],
    ['Mechanical Engineer', 'Mechanical Design Engineer', 'Product Design Engineer (Hardware)'],
    ['Robotics Engineer', 'Controls Engineer', 'Robot Programmer'],
    ['Systems Integration Engineer', 'Hardware Systems Engineer', 'Systems Architect (Hardware)'],
    ['Power Electronics Engineer', 'Power Systems Engineer (Hardware)', 'Analog Circuit Engineer'],
    ['Optical Engineer', 'Photonics Engineer', 'Laser Engineer'],

    // ---------------------------------------------------------------------------
    // 5. ENGINEERING MANAGEMENT
    // ---------------------------------------------------------------------------
    ['Engineering Manager', 'Software Engineering Manager', 'Development Manager', 'Engineering Lead'],
    ['Director of Engineering', 'Engineering Director', 'Director of Software Engineering'],
    ['Head of Platform Engineering', 'Director of Platform Engineering'],
    ['Head of Infrastructure', 'Director of Infrastructure Engineering'],
    ['Head of Security Engineering', 'Director of Security Engineering'],
    ['Group Engineering Manager', 'Senior Engineering Manager', 'Senior EM'],

    // ---------------------------------------------------------------------------
    // 6. PRODUCT MANAGEMENT
    // ---------------------------------------------------------------------------
    ['Product Manager', 'PM', 'Product Owner', 'Agile Product Owner'],
    ['Associate Product Manager', 'APM', 'Junior Product Manager'],
    ['Senior Product Manager', 'Senior PM', 'Sr. Product Manager'],
    ['Principal Product Manager', 'Staff Product Manager', 'Lead Product Manager'],
    ['Group Product Manager', 'GPM', 'Product Lead'],
    ['Director of Product', 'Product Director', 'Director of Product Management'],
    ['Technical Product Manager', 'Technical PM (Product)'],
    ['Product Analyst', 'Associate PM', 'Product Operations Analyst'],
    ['Product Operations Manager', 'Product Ops Manager', 'Head of Product Operations'],
    ['Product Strategist', 'Product Strategy Manager'],
    ['Platform Product Manager', 'Platform PM'],
    ['Growth Product Manager', 'Growth PM'],
    ['AI Product Manager', 'AI PM', 'Machine Learning Product Manager', 'ML PM'],

    // ---------------------------------------------------------------------------
    // 7. DESIGN & UX
    // ---------------------------------------------------------------------------
    [
        'UX Designer',
        'UI Designer',
        'UI/UX Designer',
        'Product Designer',
        'User Experience Designer',
        'Interaction Designer',
    ],
    ['Junior UX Designer', 'Associate UX Designer', 'Junior Product Designer'],
    ['Senior UX Designer', 'Senior Product Designer', 'Senior UI/UX Designer'],
    ['Lead Designer', 'Principal Designer', 'Staff Designer', 'UX Lead'],
    ['Design Manager', 'UX Manager', 'UI/UX Manager'],
    ['UX Researcher', 'User Researcher', 'Design Researcher', 'UX Research Specialist'],
    ['Visual Designer', 'Graphic Designer', 'Brand Designer', 'Digital Designer', 'Print Designer'],
    ['Motion Designer', 'Motion Graphics Designer', 'Animation Designer'],
    ['3D Designer', '3D Artist', 'CGI Artist'],
    ['Illustrator', 'Digital Illustrator', 'Concept Artist'],
    ['Design Systems Designer', 'Design Technologist'],
    ['Content Designer', 'UX Writer', 'UX Copywriter', 'Product Writer', 'Product Copywriter'],
    ['Creative Director', 'Head of Design', 'Design Director', 'Head of Creative'],
    ['Art Director', 'Creative Lead'],
    ['VP of Design', 'Vice President of Design', 'Chief Design Officer'],
    ['Industrial Designer', 'Human Factors Engineer', 'Ergonomics Designer'],
    ['Service Designer', 'Experience Designer', 'CX Designer'],
    ['Accessibility Designer', 'Inclusive Design Specialist'],
    ['Game Designer', 'Level Designer', 'Gameplay Designer'],
    ['Sound Designer', 'Audio Designer', 'UI Sound Designer'],

    // ---------------------------------------------------------------------------
    // 8. DATA & ANALYTICS
    // ---------------------------------------------------------------------------
    ['Data Scientist', 'Quantitative Analyst', 'Quant', 'Applied Scientist'],
    ['Data Analyst', 'Business Intelligence Analyst', 'BI Analyst', 'Insights Analyst', 'Reporting Analyst'],
    ['Data Engineer', 'Analytics Engineer', 'ETL Developer', 'ETL Engineer', 'Data Pipeline Engineer'],
    ['Data Architect', 'Data Warehouse Architect', 'Enterprise Data Architect'],
    ['BI Developer', 'Business Intelligence Developer', 'Tableau Developer', 'Power BI Developer', 'Looker Developer'],
    ['Analytics Manager', 'Director of Analytics'],
    ['SQL Analyst', 'Database Analyst'],
    ['Statistician', 'Statistical Analyst', 'Biostatistician'],
    ['Market Research Analyst', 'Research Analyst', 'Competitive Intelligence Analyst'],
    ['Risk Analyst', 'Quantitative Risk Analyst', 'Credit Risk Analyst'],
    ['Actuary', 'Actuarial Analyst', 'Actuarial Associate', 'Actuarial Consultant'],
    ['Geospatial Analyst', 'GIS Analyst', 'Geospatial Data Scientist', 'GIS Specialist'],
    ['Operations Research Analyst', 'OR Analyst', 'Optimization Analyst'],
    ['Data Governance Manager', 'Data Steward', 'Master Data Manager', 'Data Quality Manager'],
    ['Business Analyst', 'Business Systems Analyst', 'Process Analyst'],
    ['Marketing Analyst', 'Marketing Data Analyst', 'Marketing Insights Analyst'],

    // ---------------------------------------------------------------------------
    // 9. ARTIFICIAL INTELLIGENCE & MACHINE LEARNING
    // ---------------------------------------------------------------------------
    ['Machine Learning Engineer', 'ML Engineer', 'AI Engineer', 'Artificial Intelligence Engineer'],
    ['Deep Learning Engineer', 'Neural Network Engineer'],
    ['NLP Engineer', 'Natural Language Processing Engineer', 'Computational Linguist', 'NLP Scientist'],
    ['Computer Vision Engineer', 'CV Engineer', 'Image Recognition Engineer'],
    ['MLOps Engineer', 'ML Platform Engineer', 'AI Infrastructure Engineer'],
    ['AI Researcher', 'Machine Learning Researcher', 'Research Scientist (AI)', 'AI Scientist'],
    ['AI Safety Researcher', 'AI Alignment Researcher', 'Trustworthy AI Researcher'],
    ['Recommendation Systems Engineer', 'RecSys Engineer', 'Personalization Engineer'],
    ['Prompt Engineer', 'LLM Engineer', 'Generative AI Engineer', 'AI Developer', 'Foundation Model Engineer'],
    ['Conversational AI Designer', 'Chatbot Developer', 'Dialogue Systems Engineer', 'Virtual Agent Developer'],
    ['Reinforcement Learning Engineer', 'RL Engineer', 'RL Researcher'],
    ['Data Labeler', 'Data Annotator', 'AI Trainer', 'RLHF Specialist', 'Annotation Specialist'],
    ['AI Ethics Researcher', 'Responsible AI Specialist', 'AI Policy Researcher'],

    // ---------------------------------------------------------------------------
    // 10. RESEARCH & SCIENCE
    // ---------------------------------------------------------------------------
    ['Research Scientist', 'Scientist', 'Staff Scientist'],
    ['Research Engineer', 'Applied Research Engineer', 'Research Software Engineer'],
    ['Principal Scientist', 'Senior Scientist', 'Lead Scientist'],
    ['Chemist', 'Research Chemist', 'Analytical Chemist', 'Organic Chemist'],
    ['Biologist', 'Research Biologist', 'Molecular Biologist', 'Cell Biologist'],
    ['Bioinformatics Scientist', 'Computational Biologist', 'Bioinformatician'],
    ['Physicist', 'Research Physicist', 'Theoretical Physicist', 'Applied Physicist'],
    ['Materials Scientist', 'Materials Engineer', 'Materials Researcher'],
    ['Neuroscientist', 'Cognitive Scientist', 'Neuroscience Researcher'],
    ['Clinical Research Associate', 'CRA', 'Clinical Monitor', 'Clinical Research Coordinator'],
    ['Clinical Trial Manager', 'Clinical Study Manager', 'Clinical Operations Manager'],
    ['Regulatory Affairs Specialist', 'Regulatory Affairs Manager', 'Regulatory Affairs Analyst'],
    ['Epidemiologist', 'Public Health Analyst', 'Epidemiology Researcher'],
    ['Environmental Scientist', 'Environmental Analyst', 'Sustainability Scientist'],
    ['Geologist', 'Geoscientist', 'Hydrogeologist', 'Petroleum Geologist'],
    ['Astronomer', 'Astrophysicist', 'Cosmologist'],
    ['Lab Technician', 'Laboratory Technician', 'Lab Analyst', 'Research Technician'],
    ['Chemical Engineer', 'Process Chemist', 'Formulation Chemist'],
    ['Biochemist', 'Research Biochemist', 'Protein Scientist'],

    // ---------------------------------------------------------------------------
    // 11. SALES
    // ---------------------------------------------------------------------------
    ['Account Executive', 'AE', 'Sales Representative', 'Sales Rep', 'Sales Executive', 'Sales Consultant'],
    ['Junior Account Executive', 'Associate Account Executive', 'Junior Sales Representative'],
    ['Senior Account Executive', 'Senior AE', 'Enterprise Account Executive', 'Strategic Account Executive'],
    ['Account Manager', 'AM', 'Client Manager', 'Relationship Manager'],
    ['Sales Manager', 'Sales Team Lead', 'Sales Lead'],
    ['SDR Manager', 'BDR Manager', 'Sales Development Manager', 'Head of SDR'],
    [
        'Sales Development Representative',
        'SDR',
        'Business Development Representative',
        'BDR',
        'Lead Development Representative',
        'LDR',
        'Inside Sales Representative',
    ],
    ['Business Development Manager', 'BDM', 'Business Development Director', 'BD Manager'],
    ['Director of Sales', 'Sales Director', 'Regional Sales Director', 'Area Sales Director'],
    ['Sales Engineer', 'Pre-Sales Engineer', 'Technical Sales Engineer', 'Systems Engineer (Pre-Sales)'],
    ['Channel Sales Manager', 'Partner Sales Manager', 'Alliance Manager'],
    ['Key Account Manager', 'KAM', 'Strategic Account Manager', 'National Account Manager'],
    [
        'Field Sales Representative',
        'Outside Sales Representative',
        'Territory Sales Representative',
        'Territory Manager',
    ],
    ['Inside Sales Manager', 'Inside Sales Lead'],
    ['Retail Sales Associate', 'Sales Associate', 'Retail Associate', 'Store Associate', 'Sales Advisor'],
    ['Real Estate Agent', 'Realtor', 'Real Estate Broker', 'Real Estate Sales Agent', 'Property Consultant'],
    ['Financial Advisor', 'Financial Consultant', 'Wealth Manager', 'Investment Advisor', 'Wealth Advisor'],
    ['Partnerships Manager', 'Alliances Manager', 'Strategic Partnerships Manager'],

    // ---------------------------------------------------------------------------
    // 12. MARKETING
    // ---------------------------------------------------------------------------
    ['Marketing Manager', 'Marketing Lead', 'Marketing Specialist'],
    ['Marketing Director', 'Director of Marketing'],
    ['Brand Manager', 'Brand Marketing Manager'],
    ['Product Marketing Manager', 'PMM', 'Product Marketer'],
    ['Growth Manager', 'Growth Hacker', 'Growth Marketer', 'Growth Lead'],
    ['Performance Marketing Manager', 'Paid Marketing Manager', 'Digital Acquisition Manager'],
    ['Demand Generation Manager', 'Demand Gen Manager', 'Lead Generation Manager'],
    ['Digital Marketing Manager', 'Digital Marketing Specialist', 'Online Marketing Manager'],
    ['SEO Manager', 'SEO Specialist', 'Search Engine Optimization Specialist', 'SEO Analyst'],
    ['SEM Manager', 'PPC Manager', 'Paid Search Manager', 'Google Ads Manager'],
    ['Social Media Manager', 'Social Media Strategist', 'Social Media Lead'],
    ['Social Media Coordinator', 'Social Media Specialist', 'Social Media Associate'],
    ['Email Marketing Manager', 'Email Marketing Specialist', 'CRM Manager', 'Marketing Automation Manager'],
    ['Content Marketing Manager', 'Content Marketer', 'Content Strategist'],
    ['Lifecycle Marketing Manager', 'Retention Marketing Manager', 'CRM Marketing Manager'],
    ['ABM Manager', 'Account-Based Marketing Manager', 'ABM Strategist'],
    ['Customer Marketing Manager', 'Advocacy Marketing Manager'],
    ['Communications Manager', 'Corporate Communications Manager', 'Internal Communications Manager'],
    ['PR Manager', 'Public Relations Manager', 'Media Relations Manager'],
    ['Event Manager', 'Events Manager', 'Event Coordinator', 'Event Marketing Manager'],
    ['Field Marketing Manager', 'Regional Marketing Manager'],
    ['Marketing Coordinator', 'Marketing Assistant', 'Marketing Associate'],
    ['Influencer Marketing Manager', 'Influencer Relations Manager', 'Creator Partnerships Manager'],
    ['Affiliate Marketing Manager', 'Partner Marketing Manager', 'Channel Marketing Manager'],
    ['Community Manager', 'Online Community Manager', 'Brand Community Manager'],

    // ---------------------------------------------------------------------------
    // 13. CUSTOMER SUCCESS & SUPPORT
    // ---------------------------------------------------------------------------
    ['Customer Success Manager', 'CSM', 'Client Success Manager', 'Account Success Manager'],
    ['Customer Success Associate', 'CSA', 'Junior CSM', 'Customer Success Representative'],
    ['Senior Customer Success Manager', 'Senior CSM', 'Strategic Customer Success Manager'],
    ['Director of Customer Success', 'Customer Success Director', 'CS Director'],
    [
        'Customer Support Specialist',
        'Customer Service Representative',
        'Support Specialist',
        'Customer Care Specialist',
        'Customer Service Agent',
    ],
    ['Customer Support Manager', 'Customer Service Manager', 'Support Manager', 'Head of Support'],
    ['Technical Support Engineer', 'Technical Support Specialist', 'Support Engineer'],
    ['Customer Experience Manager', 'CX Manager', 'Client Experience Manager'],
    ['Customer Onboarding Manager', 'Onboarding Specialist', 'Implementation Manager'],
    ['Renewals Manager', 'Renewals Specialist', 'Retention Manager'],
    ['Developer Advocate', 'Developer Relations Manager', 'DevRel Engineer', 'Developer Evangelist'],

    // ---------------------------------------------------------------------------
    // 14. HUMAN RESOURCES & PEOPLE OPERATIONS
    // ---------------------------------------------------------------------------
    ['HR Manager', 'Human Resources Manager', 'People Manager'],
    ['HR Business Partner', 'HRBP', 'Strategic HR Partner', 'People Business Partner'],
    ['HR Generalist', 'Human Resources Generalist', 'People Generalist'],
    ['HR Coordinator', 'HR Assistant', 'Human Resources Coordinator', 'People Coordinator'],
    ['People Operations Manager', 'People Ops Manager', 'HR Operations Manager'],
    ['HR Director', 'Director of HR', 'Director of Human Resources', 'Director of People'],
    ['Compensation & Benefits Manager', 'Total Rewards Manager', 'Compensation Manager', 'Benefits Manager'],
    ['Compensation Analyst', 'Benefits Analyst', 'Total Rewards Analyst'],
    ['Learning & Development Manager', 'L&D Manager', 'Training Manager', 'Training & Development Manager'],
    [
        'Learning & Development Specialist',
        'L&D Specialist',
        'Training Specialist',
        'Corporate Trainer',
        'Learning Facilitator',
    ],
    ['Organizational Development Manager', 'OD Manager'],
    ['Employee Relations Manager', 'ER Manager', 'Labor Relations Manager'],
    ['HRIS Manager', 'HR Systems Manager', 'HR Technology Manager', 'Workday Administrator'],
    ['Payroll Manager', 'Payroll Specialist', 'Payroll Administrator'],
    ['DEI Manager', 'Diversity & Inclusion Manager', 'Diversity Manager', 'Chief Diversity Officer', 'Head of DEI'],
    ['People Analytics Analyst', 'Workforce Planning Analyst', 'Headcount Planning Analyst'],
    ['Workforce Development Manager', 'Talent Development Manager', 'People Development Manager'],

    // ---------------------------------------------------------------------------
    // 15. RECRUITING & TALENT ACQUISITION
    // ---------------------------------------------------------------------------
    ['Recruiter', 'Talent Acquisition Specialist', 'TA Specialist', 'Recruitment Specialist', 'Staffing Specialist'],
    ['Technical Recruiter', 'Engineering Recruiter', 'Tech Recruiter'],
    ['Senior Recruiter', 'Senior Talent Acquisition Specialist', 'Lead Recruiter'],
    ['Sourcer', 'Talent Sourcer', 'Recruiting Sourcer', 'Research Sourcer'],
    ['Recruiting Coordinator', 'Talent Acquisition Coordinator', 'Scheduling Coordinator (Recruiting)'],
    ['Recruiting Manager', 'Talent Acquisition Manager', 'Head of Recruiting', 'TA Manager'],
    ['Director of Recruiting', 'Director of Talent Acquisition', 'Head of Talent'],
    ['VP of Talent Acquisition', 'VP of Recruiting', 'Head of Talent Acquisition'],
    ['Campus Recruiter', 'University Recruiter', 'Graduate Recruiter', 'Early Careers Recruiter'],
    ['Executive Recruiter', 'Executive Search Consultant', 'Headhunter', 'Search Consultant'],
    ['Staffing Consultant', 'Recruitment Consultant', 'Agency Recruiter'],

    // ---------------------------------------------------------------------------
    // 16. FINANCE & ACCOUNTING
    // ---------------------------------------------------------------------------
    ['Financial Analyst', 'Finance Analyst', 'FP&A Analyst', 'Financial Planning Analyst'],
    ['Senior Financial Analyst', 'Senior FP&A Analyst', 'Senior Finance Analyst'],
    ['FP&A Manager', 'Financial Planning & Analysis Manager', 'Finance Manager'],
    ['Finance Director', 'Director of Finance'],
    ['Controller', 'Financial Controller', 'Finance Controller', 'Corporate Controller'],
    ['Chief Accountant', 'Senior Accountant', 'Accounting Supervisor'],
    ['Accountant', 'Staff Accountant', 'General Accountant', 'Junior Accountant'],
    ['CPA', 'Certified Public Accountant', 'Chartered Accountant', 'CA'],
    ['Auditor', 'Internal Auditor', 'External Auditor', 'Audit Manager', 'Audit Senior'],
    ['Tax Analyst', 'Tax Specialist', 'Tax Accountant', 'Tax Manager'],
    ['Treasurer', 'Corporate Treasurer', 'Treasury Manager'],
    ['Investment Analyst', 'Equity Research Analyst', 'Buy-Side Analyst', 'Sell-Side Analyst'],
    ['Investment Banker', 'Associate (Investment Banking)', 'IBD Analyst', 'M&A Analyst'],
    ['Private Equity Analyst', 'PE Analyst', 'Private Equity Associate'],
    ['Venture Capital Analyst', 'VC Analyst', 'VC Associate', 'Venture Associate'],
    ['Hedge Fund Analyst', 'Portfolio Analyst', 'Fund Analyst'],
    ['Portfolio Manager', 'Fund Manager', 'Investment Manager'],
    ['Risk Manager', 'Market Risk Analyst', 'Risk Management Analyst'],
    ['Compliance Officer', 'Compliance Manager', 'Regulatory Compliance Officer'],
    ['Bookkeeper', 'Accounting Clerk', 'Accounts Payable Specialist', 'Accounts Receivable Specialist'],
    ['Billing Specialist', 'Billing Analyst', 'Revenue Operations Analyst', 'RevOps Analyst'],

    // ---------------------------------------------------------------------------
    // 17. LEGAL & COMPLIANCE
    // ---------------------------------------------------------------------------
    ['Attorney', 'Lawyer', 'Associate Attorney', 'Counsel', 'Legal Counsel', 'Solicitor'],
    ['Senior Attorney', 'Senior Counsel', 'Senior Associate Attorney'],
    ['Partner (Law Firm)', 'Equity Partner', 'Non-Equity Partner'],
    ['Associate General Counsel', 'Deputy General Counsel'],
    ['Legal Director', 'Director of Legal', 'Director of Legal Affairs'],
    ['Paralegal', 'Legal Assistant', 'Legal Specialist'],
    ['Contract Manager', 'Contract Specialist', 'Contract Administrator', 'Contracts Manager'],
    ['Privacy Officer', 'Data Privacy Manager', 'DPO', 'Data Protection Officer'],
    ['Intellectual Property Analyst', 'IP Analyst', 'Patent Analyst', 'Patent Agent'],
    ['Patent Attorney', 'IP Attorney', 'Intellectual Property Attorney'],
    ['Employment Lawyer', 'Labor & Employment Attorney', 'HR Counsel'],
    ['Corporate Counsel', 'In-House Counsel', 'Company Solicitor'],
    ['Litigation Attorney', 'Trial Attorney', 'Litigator'],
    ['Legal Operations Manager', 'Legal Ops Manager', 'Director of Legal Operations'],
    ['Notary Public', 'Notary', 'Notarial Officer'],
    ['Legal Analyst', 'Legal Operations Analyst', 'Legal Process Analyst'],
    ['Compliance Analyst', 'Regulatory Analyst', 'Compliance Coordinator'],

    // ---------------------------------------------------------------------------
    // 18. OPERATIONS & STRATEGY
    // ---------------------------------------------------------------------------
    ['Operations Manager', 'Ops Manager', 'Business Operations Manager', 'General Manager'],
    ['Operations Director', 'Director of Operations'],
    ['Strategy Manager', 'Corporate Strategy Manager', 'Strategic Planning Manager'],
    ['Strategy Director', 'Director of Strategy', 'Head of Strategy'],
    ['Chief of Staff', 'Head of Special Projects', 'Strategic Advisor', 'Executive Business Manager'],
    ['Partnerships Director', 'Director of Partnerships', 'Head of Partnerships'],
    ['Revenue Operations Manager', 'RevOps Manager', 'Sales Operations Manager', 'Sales Ops Manager'],
    ['Customer Operations Manager', 'Customer Ops Manager'],
    ['Supply Chain Manager', 'Supply Chain Analyst', 'Supply Chain Coordinator'],
    ['Logistics Manager', 'Logistics Coordinator', 'Distribution Manager', 'Freight Manager'],
    ['Warehouse Manager', 'Warehouse Supervisor', 'Inventory Manager', 'Fulfillment Manager'],
    ['Procurement Manager', 'Purchasing Manager', 'Sourcing Manager', 'Strategic Sourcing Manager'],
    ['Vendor Manager', 'Vendor Relations Manager', 'Supplier Relationship Manager'],
    ['Process Improvement Manager', 'Lean Manager', 'Six Sigma Black Belt', 'Continuous Improvement Manager'],
    ['Facilities Manager', 'Workplace Manager', 'Facilities Director'],
    ['Office Manager', 'Office Administrator', 'Office Coordinator'],
    ['Business Process Manager', 'Process Manager', 'Process Excellence Manager'],

    // ---------------------------------------------------------------------------
    // 19. PROJECT & PROGRAM MANAGEMENT
    // ---------------------------------------------------------------------------
    ['Project Manager', 'Project Lead', 'Project Coordinator'],
    ['Senior Project Manager', 'Sr. Project Manager'],
    ['Program Manager', 'Senior Program Manager', 'Program Lead'],
    ['Technical Program Manager', 'TPM', 'Technical PM'],
    ['Director of Program Management', 'Director of PMO', 'Head of PMO'],
    ['Scrum Master', 'Agile Coach', 'Agile Scrum Master', 'Agile Project Manager'],
    ['PMO Analyst', 'Project Analyst', 'Project Controls Analyst'],
    ['IT Portfolio Manager', 'Portfolio Manager (PMO)'],
    ['Change Manager', 'Change Management Specialist', 'Organizational Change Manager'],
    ['Release Manager', 'Delivery Manager', 'Deployment Manager'],
    ['IT Project Manager', 'Technology Project Manager', 'Digital Project Manager'],
    ['Construction Project Manager', 'Site Project Manager', 'Civil Project Manager'],
    ['Project Scheduler', 'Scheduling Analyst', 'Planning Engineer'],

    // ---------------------------------------------------------------------------
    // 20. IT & SYSTEMS ADMINISTRATION
    // ---------------------------------------------------------------------------
    ['IT Manager', 'IT Director', 'Director of Information Technology'],
    ['Help Desk Technician', 'IT Support Specialist', 'Desktop Support Technician', 'IT Support Analyst'],
    ['IT Support Manager', 'Help Desk Manager', 'Service Desk Manager'],
    ['IT Analyst', 'IT Business Analyst', 'Systems Analyst'],
    ['ERP Analyst', 'SAP Analyst', 'Oracle Analyst'],
    ['Salesforce Administrator', 'SFDC Admin', 'Salesforce Developer', 'CRM Administrator'],
    ['Endpoint Engineer', 'Desktop Engineer', 'Workplace Engineer'],
    ['IT Vendor Manager', 'IT Procurement Manager'],
    ['Document Controller', 'Records Manager', 'Information Manager'],

    // ---------------------------------------------------------------------------
    // 21. HEALTHCARE & CLINICAL
    // ---------------------------------------------------------------------------
    ['Physician', 'Medical Doctor', 'MD', 'Doctor', 'General Practitioner', 'GP', 'Primary Care Physician'],
    ['Surgeon', 'Attending Surgeon', 'Operating Surgeon'],
    ['Specialist Physician', 'Consultant Physician', 'Medical Specialist'],
    ['Psychiatrist', 'Psychiatry Physician', 'Mental Health Physician'],
    ['Psychologist', 'Clinical Psychologist', 'Counseling Psychologist'],
    ['Therapist', 'Psychotherapist', 'Mental Health Therapist', 'Counselor', 'Licensed Counselor', 'LMFT'],
    ['Social Worker', 'Clinical Social Worker', 'LCSW', 'MSW', 'Family Services Worker'],
    ['Nurse', 'Registered Nurse', 'RN', 'Staff Nurse', 'Clinical Nurse'],
    ['Nurse Practitioner', 'NP', 'Advanced Practice Nurse', 'APN', 'APRN'],
    ['Physician Assistant', 'PA', 'PA-C'],
    ['Pharmacist', 'Clinical Pharmacist', 'Staff Pharmacist'],
    ['Pharmacy Technician', 'Pharm Tech'],
    ['Medical Assistant', 'Clinical Medical Assistant', 'Certified Medical Assistant', 'CMA'],
    ['Radiologist', 'Diagnostic Radiologist', 'Interventional Radiologist'],
    ['Radiology Technician', 'Radiographer', 'X-Ray Technician', 'MRI Technologist', 'CT Technologist'],
    ['Physical Therapist', 'PT', 'Physiotherapist'],
    ['Occupational Therapist', 'OT'],
    ['Speech-Language Pathologist', 'SLP', 'Speech Therapist'],
    ['Dentist', 'DDS', 'DMD', 'General Dentist'],
    ['Dental Hygienist', 'Registered Dental Hygienist', 'RDH'],
    ['Optometrist', 'OD', 'Eye Doctor'],
    ['Optician', 'Licensed Optician', 'Dispensing Optician'],
    ['Chiropractor', 'Doctor of Chiropractic', 'DC'],
    ['EMT', 'Emergency Medical Technician', 'Paramedic', 'First Responder'],
    ['Healthcare Administrator', 'Hospital Administrator', 'Medical Office Manager', 'Practice Manager'],
    ['Medical Coder', 'Medical Billing Specialist', 'ICD Coder', 'Healthcare Coder'],
    ['Care Coordinator', 'Patient Care Coordinator', 'Case Manager (Healthcare)'],
    ['Health Coach', 'Wellness Coach', 'Health & Wellness Coach', 'Life Coach', 'Wellness Coordinator'],
    ['Dietitian', 'Registered Dietitian', 'RD', 'Nutritionist'],
    ['Phlebotomist', 'Phlebotomy Technician', 'Blood Draw Technician'],
    ['Medical Transcriptionist', 'Medical Scribe', 'Clinical Documentation Specialist'],
    ['Prosthetist', 'Orthotist', 'Prosthetist-Orthotist'],
    ['Health Informatics Specialist', 'Clinical Informatics Analyst', 'Healthcare IT Analyst'],
    ['Infection Control Nurse', 'Infection Preventionist', 'Infection Control Officer'],

    // ---------------------------------------------------------------------------
    // 22. EDUCATION & TRAINING
    // ---------------------------------------------------------------------------
    ['Teacher', 'Educator', 'Instructor', 'Faculty Member'],
    ['Elementary School Teacher', 'Primary School Teacher', 'Grade School Teacher'],
    ['Middle School Teacher', 'Junior High Teacher'],
    ['High School Teacher', 'Secondary School Teacher', 'Secondary Educator'],
    ['Professor', 'University Professor', 'College Professor', 'Lecturer', 'Adjunct Professor'],
    ['Associate Professor', 'Assistant Professor'],
    ['Postdoctoral Researcher', 'Postdoc', 'Research Fellow', 'Postdoctoral Associate'],
    ['Teaching Assistant', 'TA', 'Graduate Teaching Assistant'],
    ['Tutor', 'Private Tutor', 'Academic Tutor'],
    ['Principal', 'School Principal', 'Headmaster', 'Headteacher'],
    ['Vice Principal', 'Assistant Principal', 'Deputy Headteacher'],
    ['Superintendent', 'School Superintendent', 'District Superintendent'],
    ['Curriculum Developer', 'Curriculum Designer', 'Curriculum Specialist'],
    ['Instructional Designer', 'Learning Designer', 'eLearning Designer', 'Course Designer'],
    ['School Counselor', 'Academic Advisor', 'Student Counselor'],
    ['Special Education Teacher', 'SPED Teacher', 'Special Needs Teacher', 'Inclusion Specialist'],
    ['ESL Teacher', 'EFL Teacher', 'English Language Teacher', 'Language Instructor', 'TESOL Teacher'],
    ['School Administrator', 'Education Administrator', 'Registrar'],
    ['Librarian', 'School Librarian', 'Academic Librarian', 'Library Director'],
    ['Education Technology Specialist', 'EdTech Specialist', 'Instructional Technology Specialist'],

    // ---------------------------------------------------------------------------
    // 23. CONSTRUCTION, ARCHITECTURE & REAL ESTATE
    // ---------------------------------------------------------------------------
    ['Architect', 'Licensed Architect', 'Registered Architect', 'Project Architect'],
    ['Interior Designer', 'Interior Architect', 'Interior Decorator'],
    ['Landscape Architect', 'Landscape Designer', 'Site Planner'],
    ['Urban Planner', 'City Planner', 'Land Use Planner', 'Urban Designer'],
    ['Civil Engineer', 'Structural Engineer', 'Geotechnical Engineer'],
    ['MEP Engineer', 'Mechanical Electrical Plumbing Engineer', 'Building Services Engineer'],
    ['BIM Manager', 'BIM Coordinator', 'Building Information Modeling Manager'],
    ['Construction Manager', 'Site Manager', 'Project Superintendent', 'Site Supervisor'],
    ['General Contractor', 'GC', 'Prime Contractor', 'Main Contractor'],
    ['Estimator', 'Cost Estimator', 'Construction Estimator', 'Quantity Surveyor'],
    ['Building Inspector', 'Construction Inspector', 'Code Inspector'],
    ['Land Surveyor', 'Survey Technician', 'Geomatics Surveyor'],
    ['Property Manager', 'Real Estate Manager', 'Building Manager'],
    ['Real Estate Analyst', 'Real Estate Financial Analyst', 'Asset Manager', 'Acquisitions Analyst'],
    ['Leasing Agent', 'Leasing Consultant', 'Leasing Manager'],
    ['Title Officer', 'Title Agent', 'Escrow Officer', 'Closing Agent'],
    ['Home Inspector', 'Property Inspector', 'Building Inspector (Residential)'],
    ['Real Estate Developer', 'Property Developer', 'Land Developer'],

    // ---------------------------------------------------------------------------
    // 24. MANUFACTURING & SUPPLY CHAIN
    // ---------------------------------------------------------------------------
    ['Manufacturing Engineer', 'Process Engineer (Manufacturing)', 'Production Engineer'],
    ['Industrial Engineer', 'IE', 'Manufacturing Systems Engineer'],
    ['Quality Engineer (Manufacturing)', 'Quality Control Engineer', 'QC Engineer'],
    ['Production Manager', 'Plant Manager', 'Manufacturing Manager'],
    ['Supply Chain Director', 'Head of Supply Chain'],
    ['Inventory Analyst', 'Inventory Planner', 'Supply Planner', 'Demand Planner'],
    ['Buyer (Procurement)', 'Purchasing Agent', 'Procurement Specialist', 'Sourcing Specialist'],
    ['QA/QC Inspector', 'Quality Inspector', 'Quality Assurance Inspector'],
    ['EHS Manager', 'Environmental Health and Safety Manager', 'Safety Manager', 'Health & Safety Manager'],
    ['Maintenance Engineer', 'Maintenance Technician', 'Maintenance Manager', 'Reliability Maintenance Engineer'],
    ['Materials Manager', 'Materials Planner', 'Materials Coordinator'],
    ['Production Planner', 'Production Scheduler', 'Manufacturing Scheduler'],
    ['Forklift Operator', 'Lift Truck Operator', 'Material Handler'],
    ['Machine Operator', 'Equipment Operator', 'Line Operator', 'CNC Operator'],
    ['Assembly Technician', 'Assembly Line Worker', 'Production Technician'],
    ['Lean Engineer', 'Continuous Improvement Engineer', 'Six Sigma Engineer', 'Kaizen Lead'],
    ['Chemical Process Engineer (Manufacturing)', 'Process Safety Engineer'],

    // ---------------------------------------------------------------------------
    // 25. HOSPITALITY & FOOD SERVICE
    // ---------------------------------------------------------------------------
    ['Hotel Manager', 'General Manager (Hotel)', 'Hospitality Manager'],
    ['Front Desk Agent', 'Guest Services Agent', 'Front Office Agent'],
    ['Concierge', 'Guest Relations Manager'],
    ['Housekeeping Manager', 'Director of Housekeeping', 'Rooms Division Manager'],
    ['Food & Beverage Manager', 'F&B Manager', 'Restaurant Manager'],
    ['Revenue Manager (Hotel)', 'Yield Manager', 'Hotel Revenue Analyst'],
    ['Executive Chef', 'Head Chef', 'Chef de Cuisine'],
    ['Sous Chef', 'Assistant Chef', 'Second Chef'],
    ['Line Cook', 'Line Chef', 'Cook', 'Kitchen Staff'],
    ['Pastry Chef', 'Pastry Cook', 'Baker'],
    ['Sommelier', 'Wine Director', 'Beverage Director'],
    ['Bartender', 'Mixologist', 'Bar Manager'],
    ['Server', 'Waiter', 'Waitress', 'Food Server', 'Service Staff'],
    ['Host', 'Hostess', 'Restaurant Host'],
    ['Catering Manager', 'Banquet Manager', 'Event Catering Manager'],
    ['Travel Agent', 'Travel Consultant', 'Tour Operator', 'Tour Guide'],
    ['Flight Attendant', 'Cabin Crew', 'Air Host', 'Air Hostess'],
    ['Pilot', 'Commercial Pilot', 'Airline Pilot', 'Captain', 'First Officer'],
    ['Spa Manager', 'Spa Director', 'Wellness Manager (Hospitality)', 'Wellness Center Manager'],
    ['Casino Manager', 'Gaming Manager', 'Casino Floor Manager'],

    // ---------------------------------------------------------------------------
    // 26. MEDIA, COMMUNICATIONS & PR
    // ---------------------------------------------------------------------------
    ['Journalist', 'Reporter', 'News Writer', 'Correspondent'],
    ['Editor', 'Managing Editor', 'Senior Editor', 'Editor-in-Chief'],
    ['Copy Editor', 'Proofreader', 'Line Editor', 'Manuscript Editor'],
    ['Broadcast Journalist', 'News Anchor', 'TV Reporter', 'Newscaster', 'News Presenter'],
    ['Podcast Producer', 'Audio Producer', 'Radio Producer'],
    ['Video Producer', 'Producer', 'TV Producer', 'Digital Producer'],
    ['Video Editor', 'Video Post-Production Specialist'],
    ['Photographer', 'Photojournalist', 'Commercial Photographer'],
    ['PR Specialist', 'Public Relations Specialist', 'Media Relations Specialist'],
    ['PR Director', 'Director of Communications', 'Head of Communications', 'Chief Communications Officer'],
    ['Publicist', 'Celebrity Publicist', 'Entertainment PR', 'Talent Publicist'],
    ['Media Buyer', 'Media Planner', 'Advertising Buyer', 'Ad Buyer'],
    ['Casting Director', 'Casting Agent', 'Casting Associate'],

    // ---------------------------------------------------------------------------
    // 27. CREATIVE & CONTENT
    // ---------------------------------------------------------------------------
    ['Copywriter', 'Advertising Copywriter', 'Creative Copywriter'],
    [
        'Technical Writer',
        'Technical Author',
        'Documentation Writer',
        'API Writer',
        'Technical Documentation Specialist',
    ],
    ['Content Writer', 'Blog Writer', 'Article Writer'],
    ['Creative Writer', 'Ghostwriter'],
    ['Animator', '2D Animator', '3D Animator', 'Motion Graphics Artist'],
    ['Videographer', 'Video Journalist', 'Corporate Videographer'],
    ['Creative Strategist', 'Creative Consultant'],
    ['Podcast Host', 'Podcaster', 'Radio Host', 'Radio Presenter'],
    ['Content Creator', 'Social Media Creator', 'Digital Creator', 'Influencer', 'YouTuber', 'Streamer'],

    // ---------------------------------------------------------------------------
    // 28. ADMINISTRATIVE & EXECUTIVE SUPPORT
    // ---------------------------------------------------------------------------
    ['Administrative Assistant', 'Admin Assistant', 'Office Assistant', 'Administrative Coordinator'],
    ['Executive Assistant', 'EA', 'Executive Administrator', 'Personal Assistant'],
    ['Receptionist', 'Front Desk Coordinator', 'Front Office Coordinator', 'Welcome Desk Coordinator'],
    ['Data Entry Specialist', 'Data Entry Clerk', 'Data Entry Operator'],
    ['Secretary', 'Administrative Secretary', 'Corporate Secretary'],
    ['Scheduler', 'Scheduling Coordinator', 'Calendar Manager'],

    // ---------------------------------------------------------------------------
    // 29. NON-PROFIT & GOVERNMENT
    // ---------------------------------------------------------------------------
    ['Executive Director (Non-Profit)', 'President (Non-Profit)', 'CEO (Non-Profit)'],
    ['Program Director', 'Program Manager (Non-Profit)', 'Program Coordinator'],
    ['Development Director', 'Director of Development', 'Fundraising Director'],
    ['Grants Manager', 'Grants Coordinator', 'Grant Writer', 'Grants Analyst'],
    ['Volunteer Coordinator', 'Volunteer Manager'],
    ['Community Outreach Coordinator', 'Outreach Manager', 'Community Engagement Manager'],
    ['Policy Analyst', 'Policy Advisor', 'Policy Researcher', 'Government Affairs Analyst'],
    ['Lobbyist', 'Government Relations Manager', 'Public Affairs Manager'],
    ['Civil Servant', 'Government Employee', 'Public Servant'],
    ['Senator', 'Congressman', 'Congresswoman', 'Legislator', 'Member of Parliament', 'MP'],
    ['Mayor', 'City Council Member', 'Alderman', 'Councilmember'],
    ['Governor', 'State Executive', 'Lieutenant Governor'],
    ['Diplomat', 'Ambassador', 'Consul', 'Foreign Service Officer'],
    ['Military Officer', 'Commissioned Officer', 'Military Commander'],
    ['Police Officer', 'Law Enforcement Officer', 'Detective', 'Sheriff', 'Deputy'],
    ['Firefighter', 'Fire Captain', 'Fire Marshal', 'Firefighter-Paramedic'],
    ['Case Worker (Government)', 'Eligibility Worker', 'Benefits Counselor'],

    // ---------------------------------------------------------------------------
    // 30. TRADES & SKILLED LABOR
    // ---------------------------------------------------------------------------
    ['Electrician', 'Journeyman Electrician', 'Master Electrician', 'Electrical Technician'],
    ['Plumber', 'Journeyman Plumber', 'Master Plumber', 'Pipefitter'],
    [
        'HVAC Technician',
        'HVAC Engineer',
        'Heating and Cooling Technician',
        'Refrigeration Technician',
        'HVAC Installer',
    ],
    ['Welder', 'Welding Technician', 'Certified Welder', 'Structural Welder', 'Pipe Welder'],
    ['Carpenter', 'Finish Carpenter', 'Framing Carpenter', 'Cabinetmaker', 'Joiner'],
    ['Mason', 'Bricklayer', 'Stonemason', 'Concrete Finisher', 'Tile Setter'],
    ['Painter (Trades)', 'Commercial Painter', 'Residential Painter', 'Industrial Painter'],
    ['Roofer', 'Roofing Contractor', 'Roofing Installer'],
    ['Mechanic', 'Automotive Technician', 'Auto Mechanic', 'Service Technician'],
    ['Aircraft Mechanic', 'Aviation Maintenance Technician', 'A&P Mechanic'],
    ['Diesel Mechanic', 'Diesel Technician', 'Heavy Equipment Mechanic'],
    ['Elevator Technician', 'Elevator Mechanic', 'Elevator Inspector'],
    ['Landscaper', 'Groundskeeper', 'Lawn Care Specialist', 'Horticulturist'],
    ['Truck Driver', 'CDL Driver', 'HGV Driver', 'OTR Driver', 'Long-Haul Driver'],
    ['Delivery Driver', 'Courier', 'Last-Mile Driver'],
    ['Bus Driver', 'Coach Driver', 'Transit Operator'],
    ['Janitor', 'Custodian', 'Custodial Worker'],
    ['Pest Control Technician', 'Exterminator', 'Pest Management Specialist'],
    ['Security Guard', 'Security Officer', 'Loss Prevention Officer'],
    ['Glazier', 'Glass Installer', 'Window Installer'],
    ['Ironworker', 'Structural Ironworker', 'Steel Erector'],
    ['Boilermaker', 'Boiler Technician', 'Pressure Vessel Technician'],

    // ---------------------------------------------------------------------------
    // 31. CONSULTING
    // ---------------------------------------------------------------------------
    ['Management Consultant', 'Strategy Consultant', 'Business Consultant'],
    ['Associate Consultant', 'Junior Consultant', 'Analyst (Consulting)'],
    ['Senior Consultant', 'Senior Associate (Consulting)'],
    ['Principal (Consulting)', 'Engagement Manager', 'Project Leader (Consulting)'],
    ['Partner (Consulting)', 'Managing Director (Consulting)', 'Senior Partner (Consulting)'],
    ['IT Consultant', 'Technology Consultant', 'Digital Consultant'],
    ['ERP Consultant', 'SAP Consultant', 'Oracle Consultant', 'Workday Consultant'],
    ['Salesforce Consultant', 'CRM Consultant'],
    ['HR Consultant', 'People Consultant', 'Organizational Design Consultant'],
    ['Financial Consultant (Advisory)', 'Finance Consultant', 'CFO Advisor'],
    ['Operations Consultant', 'Supply Chain Consultant', 'Process Consultant'],
    ['Risk Consultant', 'Risk Advisory Consultant'],
    ['Cybersecurity Consultant', 'Information Security Consultant', 'Security Advisor'],
    ['Environmental Consultant', 'Sustainability Consultant', 'ESG Consultant'],
    ['Healthcare Consultant', 'Health Consultant', 'Clinical Consultant'],

    // ---------------------------------------------------------------------------
    // 32. BANKING & FINANCIAL SERVICES
    // ---------------------------------------------------------------------------
    ['Bank Teller', 'Bank Cashier', 'Teller', 'Bank Associate'],
    ['Personal Banker', 'Relationship Banker', 'Banking Officer', 'Bank Officer'],
    ['Branch Manager (Banking)', 'Bank Branch Manager', 'Banking Center Manager'],
    ['Loan Officer', 'Mortgage Officer', 'Loan Originator', 'Mortgage Loan Originator', 'MLO'],
    ['Mortgage Broker', 'Home Loan Specialist', 'Mortgage Advisor'],
    ['Credit Analyst', 'Credit Officer', 'Credit Underwriter', 'Lending Analyst'],
    ['Underwriter', 'Loan Underwriter', 'Commercial Underwriter', 'Mortgage Underwriter'],
    ['Relationship Manager (Banking)', 'Commercial Banker', 'Corporate Banker'],
    ['Private Banker', 'Private Banking Manager', 'Wealth Relationship Manager'],
    ['Cash Management Analyst', 'Liquidity Analyst'],
    ['Trust Officer', 'Fiduciary Officer', 'Trust Advisor'],
    ['AML Analyst', 'Anti-Money Laundering Analyst', 'KYC Analyst', 'Know Your Customer Analyst', 'Fraud Analyst'],
    ['Securities Analyst', 'Equity Analyst', 'Fixed Income Analyst'],
    ['Trader', 'Equity Trader', 'Fixed Income Trader', 'Derivatives Trader', 'Quantitative Trader'],
    ['Settlement Analyst', 'Trade Settlement Specialist', 'Clearance Analyst'],

    // ---------------------------------------------------------------------------
    // 33. INSURANCE
    // ---------------------------------------------------------------------------
    ['Insurance Agent', 'Insurance Broker', 'Insurance Sales Representative', 'Insurance Advisor'],
    ['Insurance Underwriter', 'Risk Underwriter', 'Commercial Underwriter (Insurance)'],
    ['Claims Adjuster', 'Claims Analyst', 'Claims Examiner', 'Insurance Claims Specialist'],
    ['Claims Manager', 'Claims Supervisor', 'Head of Claims'],
    ['Insurance Analyst', 'Insurance Operations Analyst'],
    ['Actuary (Insurance)', 'Insurance Actuary', 'Pricing Actuary'],
    ['Loss Control Specialist', 'Risk Control Specialist', 'Loss Prevention Specialist (Insurance)'],
    ['Benefits Administrator', 'Benefits Coordinator', 'Group Benefits Specialist'],
    ['Reinsurance Analyst', 'Reinsurance Underwriter', 'Reinsurance Broker'],

    // ---------------------------------------------------------------------------
    // 34. ENERGY, OIL & GAS & RENEWABLES
    // ---------------------------------------------------------------------------
    ['Petroleum Engineer', 'Oil & Gas Engineer', 'Reservoir Engineer', 'Drilling Engineer'],
    ['Subsea Engineer', 'Offshore Engineer', 'Marine Engineer (Oil & Gas)'],
    ['Process Engineer (Energy)', 'Chemical Process Engineer (Energy)', 'Refinery Engineer'],
    ['Power Systems Engineer', 'Grid Engineer', 'Electrical Engineer (Power)'],
    ['Transmission Engineer (Power)', 'Distribution Engineer', 'Utility Engineer'],
    ['Nuclear Engineer', 'Reactor Engineer', 'Nuclear Plant Engineer'],
    ['Renewable Energy Engineer', 'Clean Energy Engineer', 'Energy Engineer'],
    ['Solar Engineer', 'Photovoltaic Engineer', 'PV Engineer', 'Solar Technician'],
    ['Wind Energy Engineer', 'Wind Turbine Engineer', 'Wind Turbine Technician'],
    ['Energy Analyst', 'Energy Consultant', 'Power Market Analyst'],
    ['Geophysicist', 'Exploration Geophysicist', 'Seismic Analyst'],
    ['Field Engineer (Oil & Gas)', 'Well Site Engineer', 'Completions Engineer'],
    ['Energy Manager', 'Head of Energy', 'Director of Energy'],

    // ---------------------------------------------------------------------------
    // 35. AEROSPACE & DEFENSE
    // ---------------------------------------------------------------------------
    ['Aerospace Engineer', 'Aeronautical Engineer', 'Aeronautics Engineer'],
    ['Avionics Engineer', 'Avionics Technician', 'Flight Systems Engineer'],
    ['Defense Analyst', 'Defense Systems Analyst'],
    ['Mission Systems Engineer', 'Spacecraft Engineer', 'Systems Engineer (Aerospace)'],
    ['Propulsion Engineer', 'Rocket Engineer', 'Jet Propulsion Engineer'],
    ['Flight Test Engineer', 'Test & Evaluation Engineer', 'T&E Engineer'],
    ['Aerostructures Engineer', 'Structural Engineer (Aerospace)', 'Stress Engineer'],
    ['GNC Engineer', 'Guidance Navigation and Control Engineer', 'Navigation Engineer'],
    ['Satellite Engineer', 'Spacecraft Systems Engineer', 'Space Systems Engineer'],
    ['Defense Acquisition Specialist', 'Government Contracting Officer', 'Defense Procurement Specialist'],
    ['Intelligence Analyst', 'Defense Intelligence Analyst', 'GEOINT Analyst', 'Geospatial Intelligence Analyst'],

    // ---------------------------------------------------------------------------
    // 36. TELECOMMUNICATIONS
    // ---------------------------------------------------------------------------
    ['Telecom Engineer', 'Telecommunications Engineer', 'Network Engineer (Telecom)'],
    ['RF Engineer (Telecom)', 'Wireless Network Engineer', '5G Engineer', 'LTE Engineer'],
    ['NOC Engineer', 'Network Operations Center Engineer', 'Network Operations Analyst'],
    ['Transmission Engineer (Telecom)', 'Transport Network Engineer', 'Fiber Optic Engineer'],
    ['Telecom Analyst', 'Telecommunications Analyst', 'Network Analyst'],
    ['VoIP Engineer', 'Unified Communications Engineer', 'UC Engineer'],
    ['Cell Tower Technician', 'Tower Climber', 'Radio Access Network Technician'],
    ['Satellite Communications Engineer', 'SATCOM Engineer'],
    ['OSS/BSS Engineer', 'Operational Support Systems Engineer', 'Business Support Systems Engineer'],

    // ---------------------------------------------------------------------------
    // 37. RETAIL & E-COMMERCE
    // ---------------------------------------------------------------------------
    ['Store Manager', 'Retail Manager', 'Shop Manager', 'Boutique Manager'],
    ['Assistant Store Manager', 'Deputy Store Manager'],
    ['Department Manager', 'Section Manager', 'Department Lead'],
    ['Category Manager', 'Merchandise Manager', 'Category Buyer'],
    ['Buyer (Retail)', 'Retail Buyer', 'Merchandise Buyer'],
    ['Merchandiser', 'Visual Merchandiser', 'Display Coordinator', 'Store Planner'],
    ['E-commerce Manager', 'Online Store Manager', 'Digital Commerce Manager'],
    ['Marketplace Manager', 'Amazon Marketplace Manager', 'Marketplace Seller'],
    ['Retail Operations Manager', 'Store Operations Manager', 'Multi-Site Manager'],
    ['Loss Prevention Manager', 'Asset Protection Manager', 'LP Manager'],
    ['Inventory Specialist', 'Stock Controller', 'Stock Manager'],
    ['Cashier', 'Checkout Operator', 'Point of Sale Operator'],
    ['Personal Shopper', 'Fashion Consultant', 'Shopping Advisor'],

    // ---------------------------------------------------------------------------
    // 38. VETERINARY & ANIMAL CARE
    // ---------------------------------------------------------------------------
    ['Veterinarian', 'Veterinary Doctor', 'Vet', 'DVM', 'VMD'],
    ['Veterinary Technician', 'Vet Tech', 'Registered Veterinary Technician', 'RVT'],
    ['Veterinary Assistant', 'Vet Assistant', 'Animal Care Technician'],
    ['Veterinary Specialist', 'Veterinary Surgeon', 'Board-Certified Veterinarian'],
    ['Zookeeper', 'Zoo Keeper', 'Animal Keeper', 'Wildlife Keeper'],
    ['Animal Trainer', 'Dog Trainer', 'Pet Trainer', 'Service Animal Trainer'],
    ['Animal Shelter Manager', 'Animal Control Officer', 'Humane Society Manager'],
    ['Equine Veterinarian', 'Large Animal Veterinarian', 'Horse Vet'],
    ['Wildlife Biologist', 'Wildlife Manager', 'Conservation Biologist'],
    ['Pet Groomer', 'Animal Groomer', 'Dog Groomer'],

    // ---------------------------------------------------------------------------
    // 39. BEAUTY, WELLNESS & PERSONAL CARE
    // ---------------------------------------------------------------------------
    ['Hair Stylist', 'Hairdresser', 'Hair Colorist', 'Colorist'],
    ['Barber', 'Master Barber', 'Barbershop Manager'],
    ['Esthetician', 'Aesthetician', 'Skin Care Specialist', 'Facialist'],
    ['Nail Technician', 'Nail Artist', 'Manicurist', 'Pedicurist'],
    ['Cosmetologist', 'Cosmetology Specialist', 'Beauty Therapist'],
    ['Massage Therapist', 'Licensed Massage Therapist', 'LMT', 'Bodywork Therapist'],
    ['Personal Trainer', 'Fitness Trainer', 'Strength Coach', 'Personal Fitness Coach'],
    ['Yoga Instructor', 'Yoga Teacher', 'Pilates Instructor', 'Group Fitness Instructor'],
    ['Makeup Artist', 'MUA', 'Beauty Artist', 'Cosmetics Artist'],
    ['Tattoo Artist', 'Body Art Specialist'],

    // ---------------------------------------------------------------------------
    // 40. SPORTS, FITNESS & RECREATION
    // ---------------------------------------------------------------------------
    ['Head Coach', 'Sports Coach', 'Head Sports Coach'],
    ['Assistant Coach', 'Coaching Coordinator'],
    ['Athletic Trainer', 'Sports Trainer', 'Team Trainer'],
    ['Sports Analyst', 'Performance Analyst', 'Sports Data Analyst', 'Video Analyst'],
    ['Sports Agent', 'Athlete Representative', 'Sports Manager', 'Talent Agent (Sports)'],
    ['Athletic Director', 'Director of Athletics', 'Sports Director'],
    ['Physical Education Teacher', 'PE Teacher', 'PE Instructor', 'Physical Education Specialist'],
    ['Recreation Coordinator', 'Recreation Manager', 'Parks & Recreation Director'],
    ['Referee', 'Umpire', 'Sports Official'],
    ['Scout', 'Sports Scout', 'Talent Scout', 'Scouting Coordinator'],
    ['Groundskeeper (Sports)', 'Turf Manager', 'Sports Field Manager'],
    ['Esports Manager', 'Esports Coach', 'Gaming Coach', 'Team Manager (Esports)'],

    // ---------------------------------------------------------------------------
    // 41. MUSIC & AUDIO
    // ---------------------------------------------------------------------------
    ['Musician', 'Session Musician', 'Instrumentalist'],
    ['Singer', 'Vocalist', 'Lead Vocalist', 'Background Vocalist'],
    ['Composer', 'Music Composer', 'Songwriter', 'Lyricist', 'Score Composer'],
    ['Music Producer', 'Record Producer', 'Beat Producer', 'Beat Maker'],
    ['Audio Engineer', 'Recording Engineer', 'Sound Engineer', 'Studio Engineer'],
    ['Mixing Engineer', 'Mix Engineer', 'Audio Mixer'],
    ['Mastering Engineer', 'Mastering Specialist'],
    ['DJ', 'Disc Jockey', 'Club DJ'],
    ['Music Director', 'Musical Director', 'Conductor', 'Orchestra Conductor'],
    ['A&R Manager', 'Artists & Repertoire Manager', 'A&R Coordinator'],
    ['Music Supervisor', 'Music Licensing Specialist', 'Sync Licensing Manager'],
    ['Label Manager', 'Record Label Manager', 'Music Label Executive'],
    ['Tour Manager', 'Road Manager', 'Live Music Manager'],
    ['Live Sound Engineer', 'FOH Engineer', 'Front of House Engineer', 'Monitor Engineer'],

    // ---------------------------------------------------------------------------
    // 42. FILM, TV & THEATRE
    // ---------------------------------------------------------------------------
    ['Film Director', 'Movie Director', 'Television Director', 'TV Director', 'Stage Director'],
    ['Film Producer', 'Executive Producer (Film)', 'Line Producer', 'Producer (Film)'],
    ['Screenwriter', 'Scriptwriter', 'Film Writer', 'TV Writer'],
    ['Cinematographer', 'Director of Photography', 'DP', 'Camera Operator'],
    ['Film Editor', 'Post-Production Editor', 'Video Editor (Film)'],
    ['Production Designer', 'Set Designer', 'Art Director (Film)'],
    ['Costume Designer', 'Wardrobe Designer', 'Wardrobe Supervisor'],
    ['Makeup Artist (Film)', 'Special Effects Makeup Artist', 'SFX Makeup Artist'],
    ['VFX Supervisor', 'Visual Effects Supervisor', 'VFX Artist'],
    ['Stunt Coordinator', 'Stunt Performer', 'Stunt Double'],
    ['Actor', 'Actress', 'Performer', 'Voice Actor', 'Screen Actor'],
    ['Stage Manager', 'Assistant Stage Manager', 'ASM'],
    ['Script Supervisor', 'Continuity Supervisor'],
    ['Location Manager', 'Location Scout', 'Production Location Coordinator'],
    ['Talent Agent', 'Theatrical Agent', 'Literary Agent (Film)'],

    // ---------------------------------------------------------------------------
    // 43. FASHION & APPAREL
    // ---------------------------------------------------------------------------
    ['Fashion Designer', 'Clothing Designer', 'Apparel Designer', 'Textile Designer'],
    ['Fashion Buyer', 'Apparel Buyer', 'Clothing Buyer'],
    ['Fashion Stylist', 'Personal Stylist', 'Celebrity Stylist', 'Editorial Stylist'],
    ['Fashion Photographer', 'Editorial Photographer'],
    ['Visual Merchandiser (Fashion)', 'Fashion Merchandiser'],
    ['Fashion Director', 'Creative Director (Fashion)', 'Style Director'],
    ['Pattern Maker', 'Patternmaker', 'Pattern Cutter', 'Technical Designer (Fashion)'],
    ['Textile Engineer', 'Fabric Technologist', 'Materials Developer (Apparel)'],
    ['Trend Forecaster', 'Trend Analyst', 'Fashion Trend Researcher'],
    ['Tailor', 'Seamstress', 'Alterations Specialist', 'Bespoke Tailor'],
    ['Fashion Brand Manager', 'Brand Manager (Fashion)'],

    // ---------------------------------------------------------------------------
    // 44. PUBLISHING & BOOKS
    // ---------------------------------------------------------------------------
    ['Publisher', 'Book Publisher', 'Publishing Director', 'Editorial Director'],
    ['Acquisitions Editor', 'Commissioning Editor', 'Book Editor'],
    ['Developmental Editor', 'Structural Editor', 'Manuscript Editor (Publishing)'],
    ['Literary Agent', 'Book Agent', 'Talent Agent (Publishing)'],
    ['Author', 'Writer', 'Book Author', 'Novelist'],
    ['Editorial Assistant', 'Publishing Assistant', 'Editorial Coordinator'],
    ['Rights Manager', 'Rights & Permissions Manager', 'Licensing Manager (Publishing)'],
    ['Publicist (Publishing)', 'Book Publicist', 'Literary Publicist'],
    ['Production Editor (Publishing)', 'Managing Editor (Publishing)', 'Print Production Manager'],
    ['Translator', 'Literary Translator', 'Language Specialist'],

    // ---------------------------------------------------------------------------
    // 45. CHILDCARE & EARLY EDUCATION
    // ---------------------------------------------------------------------------
    ['Childcare Worker', 'Childcare Provider', 'Daycare Worker', 'Daycare Aide'],
    ['Nanny', 'Au Pair', 'Live-In Nanny', 'Babysitter'],
    ['Early Childhood Educator', 'ECE Teacher', 'Preschool Teacher', 'Pre-K Teacher', 'Kindergarten Teacher'],
    ['Daycare Director', 'Childcare Center Director', 'Early Learning Director'],
    ['Child Development Specialist', 'Early Childhood Development Specialist'],
    ['Family Advocate', 'Family Support Worker', 'Parent Educator'],
    ['Child Life Specialist', 'Certified Child Life Specialist', 'CCLS'],
];

export default JOB_TITLE_SYNONYM_GROUPS;
